/*
 *   File : dashboard.js
 *   Author : https://evoqins.com
 *   Description : Country admin Home page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from "react";
import { DataTableComponent } from "../../../Component/DataTable";
import { Loader } from "../../../Component/Others";
import { APIService } from "../../../Service";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useSelector } from "react-redux";
import { DownloadButton, PrimaryButton, TextButton } from "../../../Component/Buttons";
import { CustomSearchInput, CustomTextInput, DateRangePicker } from "../../../Component/FormElements";
import { EmptyScreen } from "../../../Component/EmptyComponent";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 20;

const STORES_SORT_FIELDS = [
    'store_id',
    'store_name',
    'total_liters_sold',
    'points_earned',
];

const SPECIALISTS_SORT_FIELDS = [
    'customer_id',
    'username',
    'store_name',
    'total_liters_sold',
    'premium_liters_sold',
    'points_earned',
];

const TopSellingPage = () => {
    const today = new Date();
    const { state } = useLocation();
    const navigate = useNavigate();

    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);
    const [currentPage, setCurrentPage] = useState(1);

    const [topSellingData, setTopSellingData] = useState({});
    const [loader, setLoader] = useState(true);

    const [selectedDate, setSelectedDate] = useState(null);
    const [searchValue, setSearchValue] = useState("");

    const [downloadLoader, setDownloadLoader] = useState(false);
    const [clearFilter, setClearFilter] = useState(false);
    const [toggleFilter, setToggleFilter] = useState(false);


    const [litre, setLitre] = useState({ min: "", max: "" });
    const [point, setPint] = useState({ min: "", max: "" });

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const allowedSortFields = state?.filter_id === 2
        ? SPECIALISTS_SORT_FIELDS
        : STORES_SORT_FIELDS;

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    }, []);

    // Reset sort when switching Stores / Sales specialists view
    useEffect(() => {
        setSortField(null);
        setSortDirection(null);
        setCurrentPage(1);
    }, [state?.filter_id]);

    useEffect(() => {
        if (state !== null) {
            if (skipListFetchRef.current) {
                skipListFetchRef.current = false;
                return;
            }
            _getTopSellingData();
        } else {
            navigate("/");
        }
    }, [state, searchValue, currentPage, clearFilter]);

    // handle page change 
    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !allowedSortFields.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoader(true);

        // Only skip the list useEffect when page will actually change.
        // If already on page 1, useEffect won't run and a stuck skip flag
        // would block the next pagination API call (loader stuck).
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getTopSellingData(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const _handleClearAll = () => {

        // setSelectedDate([startOfMonth(today), endOfMonth(today)]);
        setLitre({ min: "", max: "" });
        setPint({ min: "", max: "" });

        setSortField(null);
        setSortDirection(null);
        setClearFilter(true);
        setLoader(true);
        setCurrentPage(1);
    };

    // API - Dash board data
    const _getTopSellingData = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {

        const formatted_start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd');
        const formatted_end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd');

        const effective_sort_by = sort_by && allowedSortFields.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'CA/dashboard/get-top-selling';
        let data = {
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "query": searchValue,

            "scope": 1,
            "filter": state?.filter_id || null,
            "start_date": formatted_start_date,
            "end_date": formatted_end_date,
            "min_litres": Number(litre.min) || null,
            "max_litres": Number(litre.max) || null,
            "min_points": Number(point.min) || null,
            "max_points": Number(point.max) || null,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setTopSellingData(response.data);
            } else {
                setTopSellingData({});
            }
            setLoader(false);
        });
    };


    // API - Download
    const _download = () => {
        setDownloadLoader(true);

        const formatted_start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd');
        const formatted_end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd');

        const effective_sort_by = sortField && allowedSortFields.includes(sortField) ? sortField : null;
        const effective_sort_order = effective_sort_by ? sortDirection : null;

        let is_post = true;
        let url = 'CA/dashboard/get-top-selling/download';
        let data = {
            "scope": 1,
            "filter": state?.filter_id || null,
            "start_date": formatted_start_date,
            "end_date": formatted_end_date,
            "min_litres": Number(litre.min) || null,
            "max_litres": Number(litre.max) || null,
            "min_points": Number(point.min) || null,
            "max_points": Number(point.max) || null,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };

    const litresLabel = PROFILE_DATA.country === "Egypt" ? 'Total units' : "Total Ltrs";
    const premiumLitresLabel = PROFILE_DATA.country === "Egypt" ? 'Premium units' : "Premium Ltrs";

    // Stores view (filter 1)
    const TABLE_COLUMNS_STORES = [
        {
            name: 'Store ID',
            id: 'store_id',
            sortField: 'store_id',
            selector: row => row.store_id || "-",
            width: "120px",
            sortable: true
        },
        {
            name: 'Store',
            id: 'store_name',
            sortField: 'store_name',
            selector: row => row.store_name,
            width: "280px",
            sortable: true
        },
        {
            name: litresLabel,
            id: 'total_liters_sold',
            sortField: 'total_liters_sold',
            selector: row => row.Total_earning || row.total_liters_sold,
            width: "160px",
            sortable: true
        },
        {
            name: 'Points earned',
            id: 'points_earned',
            sortField: 'points_earned',
            selector: row => row.points_earned || "-",
            width: "140px",
            sortable: true
        },
    ];

    // Sales specialists view (filter 2)
    const TABLE_COLUMNS_SPECIALISTS = [
        {
            name: 'Sales specialist name',
            id: 'username',
            sortField: 'username',
            selector: row => row.username,
            width: "220px",
            sortable: true
        },
        {
            name: 'Store',
            id: 'store_name',
            sortField: 'store_name',
            selector: row => row.store_name || "-",
            cell: row => <>{row.store_name}</>,
            width: "260px",
            sortable: true
        },
        {
            name: litresLabel,
            id: 'total_liters_sold',
            sortField: 'total_liters_sold',
            selector: row => row.Total_earning || row.total_liters_sold,
            width: "140px",
            sortable: true
        },
        {
            name: premiumLitresLabel,
            id: 'premium_liters_sold',
            sortField: 'premium_liters_sold',
            selector: row => row.premium_liters_sold || "-",
            width: "150px",
            sortable: true
        },
        {
            name: 'Points earned',
            id: 'points_earned',
            sortField: 'points_earned',
            selector: row => row.points_earned || "-",
            width: "140px",
            sortable: true
        },
    ];

    const TABLE_COLUMNS = state?.filter_id === 2
        ? TABLE_COLUMNS_SPECIALISTS
        : TABLE_COLUMNS_STORES;

    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <div className="d-flex align-items-center justify-content-between">
                    <div className='e-display-flex cursor-pointer w-max-content'
                        onClick={() => navigate("/")} >
                        <img src={require("../../../Assets/Image/CountryAdmin/arrow-left.svg").default}
                            draggable={false}
                            className='cursor-pointer'
                            alt='arrow'
                        />
                        <h1 className='ps-1 mb-0  e-primary-font-playfair e-font-24 line-height-32px e-text-raisin-black e-font-weight-600'>
                            Top Selling
                        </h1>
                    </div>


                    <DateRangePicker
                        placeholder="Select date range"
                        calendarWrapperClass="end-0"
                        value={selectedDate}
                        maxDate={new Date}
                        activePresentId={null}
                        handleClear={() => setSelectedDate(null)}
                        handleChange={(date) => setSelectedDate(date)}
                        handleApply={() => {
                            _getTopSellingData(1);
                            setLoader(true);
                        }} />
                </div>
            </div>
            <div className='margin-neg-66px-top padding-52px-lr '>
                <div className='e-bg-white e-admin-box-shadow py-4 e-border-radius-16 e-border-1px-platinum-dark'>
                    <div className='d-flex justify-content-end px-4'>


                        <div className='d-flex align-items-center gap-4'>

                            <DownloadButton loader={downloadLoader}
                                disabled={loader || Object.keys(topSellingData).length === 0 || topSellingData?.data?.length === 0}
                                onClick={_download} />

                            <CustomSearchInput placeholder={"Search by name,ID or store"}
                                type="text"
                                require
                                value={searchValue}
                                className="w-100 padding-8px-tb py-2 "
                                labelStyle="e-font-weight-500"
                                iconClass="top-10px"
                                handleChange={(e) => setSearchValue(e)}
                                handleClear={() => { setSearchValue("") }} />

                            <img src={toggleFilter ? require("../../../Assets/Image/filter-icon-white.svg").default : require("../../../Assets/Image/filter-icon.svg").default}
                                draggable={false}
                                // height={38}
                                // width={38}
                                className={`${toggleFilter ? `e-bg-indigo` : ``} transition e-border-1px-anti-flash-white e-border-radius-8 p-2 cursor-pointer`}
                                alt='filter'
                                onClick={() => setToggleFilter(!toggleFilter)} />
                        </div>
                    </div>



                    {/* filter */}
                    {toggleFilter &&
                        <div className='e-bg-anti-flash-white-dark py-3 px-4 mt-3'>
                            <div className='d-flex align-items-center gap-4'>
                                <div >
                                    <p className="padding-6px-bottom mb-0 e-text-dark-black e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-20px">
                                        Total ltrs
                                    </p>
                                    <div className="d-flex align-items-center gap-3">
                                        <CustomTextInput label="Min litres"
                                            placeholder="100"
                                            type="number"
                                            value={litre.min}
                                            className="w-100 e-border-radius-4-filter"
                                            borderClass="w-70px e-border-radius-4-filter"
                                            labelStyle="e-font-weight-500 e-text-raisin-black"
                                            height="h-auto"
                                            wrapperClass="d-flex align-items-center gap-1"
                                            padding="p-2"
                                            handleChange={(e) => {
                                                setLitre((prev) => ({
                                                    ...prev,
                                                    min: e
                                                }));
                                            }} />

                                        <CustomTextInput label="Max litres"
                                            placeholder="100"
                                            type="number"
                                            value={litre.max}
                                            className="w-100 e-border-radius-4-filter"
                                            borderClass="w-70px e-border-radius-4-filter"
                                            labelStyle="e-font-weight-500 e-text-raisin-black"
                                            height="h-auto"
                                            wrapperClass="d-flex align-items-center gap-1"
                                            padding="p-2"
                                            handleChange={(e) => {
                                                setLitre((prev) => ({
                                                    ...prev,
                                                    max: e
                                                }));
                                            }} />
                                    </div>
                                </div>

                                <div >
                                    <p className="padding-6px-bottom mb-0 e-text-dark-black e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-20px">
                                        Points
                                    </p>
                                    <div className="d-flex align-items-center gap-3">
                                        <CustomTextInput label="Min points"
                                            placeholder="100"
                                            type="number"
                                            value={point.min}
                                            className="w-100 e-border-radius-4-filter"
                                            borderClass="w-70px e-border-radius-4-filter"
                                            labelStyle="e-font-weight-500 e-text-raisin-black"
                                            height="h-auto"
                                            wrapperClass="d-flex align-items-center gap-1"
                                            padding="p-2"
                                            handleChange={(e) => {
                                                setPint((prev) => ({
                                                    ...prev,
                                                    min: e
                                                }));
                                            }} />

                                        <CustomTextInput label="Max points"
                                            placeholder="100"
                                            type="number"
                                            value={point.max}
                                            className="w-100 e-border-radius-4-filter"
                                            borderClass="w-70px e-border-radius-4-filter"
                                            labelStyle="e-font-weight-500 e-text-raisin-black"
                                            height="h-auto"
                                            wrapperClass="d-flex align-items-center gap-1"
                                            padding="p-2"
                                            handleChange={(e) => {
                                                setPint((prev) => ({
                                                    ...prev,
                                                    max: e
                                                }));
                                            }} />
                                    </div>
                                </div>

                                <div className='d-flex gap-3 align-items-center justify-content-end mt-auto'>
                                    <TextButton name="Clear all"
                                        onPress={_handleClearAll} />
                                    <PrimaryButton name="Filter now"
                                        className="px-3 py-1 e-border-radius-8"
                                        onPress={() => {
                                            _getTopSellingData(1);
                                            setLoader(true);
                                        }} />
                                </div>

                            </div>
                        </div>
                    }

                    {loader ?
                        // loader 
                        <Loader />
                        :
                        <>
                            {/* Registered members list table */}
                            {Object.keys(topSellingData).length !== 0 && topSellingData?.data?.length !== 0 ?
                                <div className='e-members-table'>
                                    <DataTableComponent
                                        key={`top-selling-${state?.filter_id}-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={TABLE_COLUMNS}
                                        data={topSellingData.data}
                                        pagination={true}
                                        paginationServer={true}
                                        paginationTotalRows={topSellingData.total_count}
                                        paginationPerPage={ITEMS_PER_PAGE}
                                        // selectableRows={true}
                                        paginationDefaultPage={currentPage}
                                        paginationResetDefaultPage={false}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange} />
                                </div>
                                :
                                // empty screen
                                <div className='h-100vh pt-5 mt-5'>
                                    <EmptyScreen message="No sales specialist found" />
                                </div>
                            }
                        </>
                    }
                </div>
            </div>

        </section>
    )
}

export default TopSellingPage;
